import {
  Snackbar,
  Alert,
  Box,
  Typography,
  TextField,
  Button,
  Tooltip,
  Divider,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share'
import { IconCopy } from '@tabler/icons-react'
import RoundedButton from '../../../../components/ui/RoundedButton'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { trackRestaurantShare } from '../../../../services/rewardsService'
import { logEvent } from '../../../../analytics/analytics'
import { errorLog } from '../../../../utils/log-helper'
import { useDevice } from '../../../../hooks/useDevice'
import {
  RestaurantShareFacebookIcon,
  RestaurantShareIconShare,
  RestaurantShareMenu,
  RestaurantShareXIcon,
  RestaurantShareLinkedInIcon,
  RestaurantShareInstagramIcon,
} from '../styled/RestaurantShare.styled'

const RESACTLY_HANDLE = '@Resactly'

const cityToHashtag = (city?: string): string | null => {
  if (!city) return null
  const c = city.trim().toLowerCase()
  // allowlist (feel free to add a few more)
  if (c === 'new york' || c === 'nyc' || c.includes('new york'))
    return '#NYCFood'
  if (c === 'los angeles' || c === 'la' || c.includes('los angeles'))
    return '#LAEats'
  if (c === 'san francisco' || c === 'sf' || c.includes('san francisco'))
    return '#SFEats'
  if (c === 'chicago' || c === 'chi' || c.includes('chicago'))
    return '#ChicagoFood'
  if (c === 'miami' || c.includes('miami')) return '#MiamiFood'
  return null
}

interface RestaurantShareProps {
  restaurantId: string
  restaurantHandle?: string | null
  imgUrl: string
  description: string
  title: string
  cuisine: string
  city: string
  url?: string
  /**
   * Fired when the share control is opened. Lets an embedding surface (e.g. the restaurant card)
   * attribute a "share opened" tap without owning the menu.
   */
  onOpen?: () => void
  /**
   * Suppress the SEO <Helmet> (title/OG meta). The detail page wants it, but when many share
   * controls are embedded on one page (e.g. a card feed) each Helmet would fight over the document
   * title — so embedders pass this to render the control without the meta side effects.
   */
  suppressHelmet?: boolean
}

const RestaurantShare: React.FC<RestaurantShareProps> = ({
  restaurantId,
  restaurantHandle,
  imgUrl,
  description,
  title,
  cuisine,
  city,
  url,
  onOpen,
  suppressHelmet = false,
}) => {
  const { theme, location } = useAppUtilities()
  const { isHybrid } = useDevice()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [pageTitle, setPageTitle] = useState('')
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const open = Boolean(anchorEl)
  const shareUrl = `https://www.resactly.com${
    url?.length ? url : location.pathname
  }`
  const image = imgUrl

  // Generate caption for Instagram with hashtags
  const baseHashtags = ['#Resactly', '#Foodie']
  const cityHashtag = cityToHashtag(city)
  const hashtags = [
    ...baseHashtags,
    ...(cityHashtag ? [cityHashtag] : []),
  ].join(' ')
  const caption = `Ate at ${title} 🍽️✨ Found it via ${RESACTLY_HANDLE}.${
    restaurantHandle ? ` Tag ${restaurantHandle} too!` : ''
  }

${shareUrl}

${hashtags}`

  useEffect(() => {
    setPageTitle(`${title} - Authentic ${cuisine} Dining Experience in ${city}`)
  }, [title, cuisine, city])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    // Stop propagation so opening share from an embedding surface (e.g. a restaurant card whose
    // wrapper navigates on click) never triggers that surface's navigation.
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    onOpen?.()
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const logSocialShare = (method: string): void => {
    logEvent('social_share', {
      restaurant_id: restaurantId,
      method,
      screen_name: 'restaurant_detail',
    })
  }

  const handleShareClick = (channel: 'facebook' | 'x' | 'linkedin'): void => {
    // Fire-and-forget tracking call
    void trackRestaurantShare(restaurantId, channel).catch((error) => {
      errorLog('Share tracking failed', error)
    })
    logSocialShare(channel)
    // Close menu after share action
    handleClose()
  }

  const handleInstagramClick = (): void => {
    // Fire-and-forget tracking
    void trackRestaurantShare(restaurantId, 'instagram').catch((error) => {
      errorLog('Share tracking failed', error)
    })
    logSocialShare('instagram')

    // Best-effort attempt to open Instagram
    if (isHybrid) {
      // Mobile: try deep link
      try {
        window.location.href = 'instagram://app'
        // If deep link fails, fallback to web after a delay
        setTimeout(() => {
          window.open(
            'https://www.instagram.com/',
            '_blank',
            'noopener,noreferrer',
          )
        }, 500)
      } catch (error) {
        // Fallback to web
        window.open(
          'https://www.instagram.com/',
          '_blank',
          'noopener,noreferrer',
        )
      }
    } else {
      // Desktop: open Instagram website
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer')
    }
    // Don't close menu immediately - let user see caption/link are still available
  }

  const copyCaption = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(caption)
      setToastMessage('Caption copied!')
      // Fire-and-forget tracking
      void trackRestaurantShare(restaurantId, 'instagram').catch((error) => {
        errorLog('Share tracking failed', error)
      })
      logSocialShare('copy_caption')
      // Keep menu open so user can still choose platform
    } catch (error) {
      errorLog('Failed to copy caption', error)
    }
  }

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setToastMessage('Link copied!')
      // Fire-and-forget tracking
      void trackRestaurantShare(restaurantId, 'copy_link').catch((error) => {
        errorLog('Share tracking failed', error)
      })
      logSocialShare('copy_link')
      // Keep menu open so user can still choose platform
    } catch (error) {
      errorLog('Failed to copy link', error)
    }
  }

  const handleToastClose = (): void => {
    setToastMessage(null)
  }

  return (
    <>
      {!suppressHelmet && (
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={description} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={image} />
          <meta property="og:url" content={shareUrl} />
          <meta name="x:card" content="summary_large_image" />
          <meta name="x:title" content={pageTitle} />
          <meta name="x:description" content={description} />
          <meta name="x:image" content={image} />
        </Helmet>
      )}
      <RoundedButton
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        borderColor={theme.palette.grey[300]}
        diameter={37}
      >
        <RestaurantShareIconShare />
      </RoundedButton>
      <RestaurantShareMenu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {/* Caption Panel - Always visible */}
        <Box sx={{ p: 2, minWidth: 320 }} onClick={(e) => e.stopPropagation()}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Caption
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={caption}
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
              },
            }}
          />
          <Box display="flex" flexDirection="column" gap={1}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<IconCopy size={18} />}
              onClick={() => {
                void copyCaption()
              }}
              fullWidth
            >
              Copy caption
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<IconCopy size={18} />}
              onClick={() => {
                void copyLink()
              }}
              fullWidth
            >
              Copy link
            </Button>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1.5, display: 'block', fontStyle: 'italic' }}
          >
            Paste caption into your post and tag @Resactly.
          </Typography>
        </Box>

        <Divider />

        {/* Platform Icons Row */}
        <Box sx={{ p: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1.5, px: 0.5 }}
          >
            Share to
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Tooltip title="Facebook" placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => handleShareClick('facebook')}
              >
                <FacebookShareButton
                  url={shareUrl}
                  title={title}
                  className="flex-center"
                >
                  <RestaurantShareFacebookIcon />
                </FacebookShareButton>
              </Box>
            </Tooltip>
            <Tooltip title="X" placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => handleShareClick('x')}
              >
                <TwitterShareButton
                  className="flex-center"
                  url={shareUrl}
                  title={title}
                >
                  <RestaurantShareXIcon />
                </TwitterShareButton>
              </Box>
            </Tooltip>
            <Tooltip title="LinkedIn" placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => handleShareClick('linkedin')}
              >
                <LinkedinShareButton
                  className="flex-center"
                  url={shareUrl}
                  title={title}
                >
                  <RestaurantShareLinkedInIcon />
                </LinkedinShareButton>
              </Box>
            </Tooltip>
            <Tooltip title="Instagram" placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                onClick={handleInstagramClick}
              >
                <RestaurantShareInstagramIcon />
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </RestaurantShareMenu>
      <Snackbar
        open={toastMessage !== null}
        autoHideDuration={3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleToastClose}
          severity="success"
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default RestaurantShare
